import { useEffect, useState } from 'react';
import { ToastType } from '../../types/types';

type GeolocateState = 'idle' | 'locating' | 'granted' | 'denied';

interface GeolocateButtonProps {
  showToast: (message: string, type: ToastType) => void;
}

export default function GeolocateButton({ showToast }: GeolocateButtonProps) {
  const [geolocateState, setGeolocateState] = useState<GeolocateState>('idle');

  useEffect(() => {
    const handler = (e: Event) => {
      const state = (e as CustomEvent<{ state: GeolocateState }>).detail.state;
      setGeolocateState(state);
    };
    window.addEventListener('geolocateStateChange', handler);
    return () => window.removeEventListener('geolocateStateChange', handler);
  }, []);

  const handleClick = () => {
    if (geolocateState === 'denied') {
      showToast('location blocked — enable it in your browser settings', 'error');
      return;
    }
    window.dispatchEvent(new CustomEvent('triggerGeolocation'));
  };

  return (
    <>
      <div
        onClick={handleClick}
        style={{
          position: 'fixed',
          bottom: '80px',
          right: '10px',
          width: '40px',
          height: '40px',
          borderRadius: '50%',
          backgroundColor: 'var(--color-surface)',
          border: geolocateState === 'denied' ? '2px solid var(--color-error)' : 'none',
          boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          cursor: 'pointer',
          zIndex: 9980,
          transition: 'background-color 0.2s ease',
        }}
      >
        {geolocateState === 'locating' ? (
          <span
            style={{
              width: '16px',
              height: '16px',
              border: '2px solid rgba(0,0,0,0.15)',
              borderTopColor: 'var(--color-location)',
              borderRadius: '50%',
              display: 'inline-block',
              animation: 'spin 0.75s linear infinite',
            }}
          />
        ) : (
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={geolocateState === 'denied' ? 'var(--color-error)' : 'var(--color-location)'}
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <circle
              cx="12"
              cy="12"
              r="3"
              fill={geolocateState === 'granted' ? 'var(--color-location)' : 'none'}
            />
            <path d="M12 2v3M12 19v3M2 12h3M19 12h3" />
          </svg>
        )}
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    </>
  );
}
